import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { BookingStatus } from "@repo/database";
import {
  addDays,
  addMonths,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { ArrowLeftIcon, ArrowRightIcon, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useBooking } from "~/hooks/use-booking";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";

interface MonthCalendarProps {
  onDayClick?: (date: Date) => void;
  selectedDate?: Date | null;
  renderDayContent?: (date: Date) => React.ReactNode;
  onClose?: () => void;
}

const MonthCalendar: React.FC<MonthCalendarProps> = ({
  onDayClick,
  selectedDate,
  renderDayContent,
  onClose,
}) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  const { t } = useTranslation("common");

  const startOfCurrentMonth = startOfMonth(currentMonth);
  const { bookings } = useBooking();
  // Generate all days in a 6-week calendar grid
  const days: Date[] = [];
  let day = startOfWeek(startOfCurrentMonth);

  for (let i = 0; i < 42; i++) {
    days.push(day);
    day = addDays(day, 1);
  }

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const renderDays = () => {
    return days.map((day, index) => {
      const isToday = isSameDay(day, new Date());
      const isCurrentMonth = isSameMonth(day, currentMonth);
      const isSelected = selectedDate && isSameDay(day, selectedDate);

      const bookingsToday = bookings.filter(
        (booking) =>
          isSameDay(booking.startTime, day) &&
          booking.bookingStatus !== BookingStatus.canceled &&
          booking.bookingStatus !== BookingStatus.no_show,
      );
      const guestsToday = bookingsToday.reduce(
        (acc, curr) => acc + curr.groupSize,
        0,
      );
      return (
        <div
          key={index}
          className={cn(
            "relative p-2 border hover:bg-background-secondary/70 cursor-pointer bg-background-secondary text-foreground h-28 flex flex-col justify-start items-start border-gray-300",
            !isCurrentMonth && "bg-background text-muted-foreground",
            isSelected && "border-blue-500",
          )}
          onClick={() => onDayClick && onDayClick(day)}
        >
          {guestsToday > 0 && (
            <div className="text-xs rounded-lg bg-blue-400 px-2 py-1 min-w-[50px] text-black">
              {guestsToday} {t("Booking.guests")}
            </div>
          )}
          <div
            className={cn(
              "absolute flex items-center justify-center top-2 right-2 text-sm font-semibold",
              isSelected && "rounded-full bg-blue-500 w-7 h-7 text-white",
              isToday && "rounded-full bg-white w-7 h-7 text-black",
            )}
          >
            {format(day, "d")}
          </div>
          <div className="mt-6 w-full">{renderDayContent?.(day)}</div>
        </div>
      );
    });
  };

  useEffect(() => {
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    });
  }, [onClose]);

  return (
    <div className="w-full mx-auto shadow-lg bg-background">
      <div className="flex justify-between items-center mb-4 p-4 sticky top-0 z-10 bg-background">
        <div className="flex items-center gap-2">
          <Calendar />
          <h2 className="text-lg font-semibold text-foreground">
            {format(currentMonth, "MMMM yyyy")}
          </h2>
        </div>

        <div className="flex flex-row gap-2">
          <Button onClick={handlePrevMonth}>
            <ArrowLeftIcon />
          </Button>
          <Button onClick={handleNextMonth}>
            <ArrowRightIcon />
          </Button>
          <Button onClick={onClose}>
            <CloseIcon />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2 p-4">
        <div className="grid grid-cols-7 text-center text-sm text-muted-foreground font-medium mb-2">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div key={day} className="py-1">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">{renderDays()}</div>
      </div>
    </div>
  );
};

export default MonthCalendar;
